import { useSelector } from 'react-redux';
import { useMyForm } from '../../../../../../../react-commons/hooks/useMyForm';
import { QueryBaseComponent } from '../../../../../../../react-commons/query/QueryBaseComponent';
import { ResourceType } from '../../../../../resource/enumerations/ResourceType';
import {
    defaultImageAttachmentResource,
    ImageAttachmentMutationResourceSchema,
    ImageAttachmentPropertyValidation,
    useImageAttachmentActions,
} from '../../../../../resource/resources/artifacts/attachment/imageAttachmentResource';
import { Form_DisplayComponentBaseView } from '../../../../reusable-components/form-components/Form_DisplayComponentBaseView';
import { SingleParentedOverviewComponent } from '../../../../reusable-components/resource-overviews/SingleParentedOverviewComponent';
import { selectNoScopeValidationScope, selectReadOnly } from '../../../../util/useSelectorFunctions';
import { ImageAttachmentContent } from './ImageAttachmentContent';

const ImageAttachmentOverviewContent = ({ id, displayedInOverview }: { id: string; displayedInOverview: boolean }) => {
    const readOnly = useSelector(selectReadOnly);

    const imageAttachmentActions = useImageAttachmentActions();
    const imageAttachmentQuery = imageAttachmentActions.useGetResource(id);
    const updateImageAttachment = imageAttachmentActions.useUpdateResource();

    const { form, formNames, onSubmit } = useMyForm({
        schema: ImageAttachmentMutationResourceSchema,
        defaultValues: defaultImageAttachmentResource,
        submitHandler: async (data) => {
            await updateImageAttachment.mutateAsync({
                resource: data,
                id: data.id,
            });
        },
        reactiveValues: imageAttachmentQuery.data,
    });

    return (
        <QueryBaseComponent name="ImageAttachment" query={imageAttachmentQuery}>
            <Form_DisplayComponentBaseView onSubmit={onSubmit} form={form} canSubmitDefaultValue={false}>
                <ImageAttachmentContent
                    displayedInOverview={displayedInOverview}
                    form={form}
                    formNames={formNames}
                    readOnly={readOnly}
                    id={id}
                />
            </Form_DisplayComponentBaseView>
        </QueryBaseComponent>
    );
};

export const ImageAttachmentOverviewComponent = ({ parentId }: { parentId: string }) => {
    const validationScope = useSelector(selectNoScopeValidationScope);

    const imageAttachmentActions = useImageAttachmentActions();

    const imageAttachmentsQuery = imageAttachmentActions.useGetAllResources(parentId);

    const resourceType = ResourceType.IMAGE_ATTACHMENT;

    return (
        <QueryBaseComponent name="Image Attachments" query={imageAttachmentsQuery}>
            <SingleParentedOverviewComponent
                label={'Image Attachment'}
                resourceQuery={imageAttachmentsQuery}
                content={(id, displayedInOverview) => (
                    <ImageAttachmentOverviewContent id={id} displayedInOverview={displayedInOverview} />
                )}
                propertyValidationInformation={{
                    namePropertyIdentifier: ImageAttachmentPropertyValidation.NAME,
                    resourceType,
                }}
                validationScope={validationScope}
                parentId={parentId}
            />
        </QueryBaseComponent>
    );
};
